import React, { useState, useMemo } from 'react';
import { Row, Col, Card, Typography, Progress, Spin, Tooltip, Alert, Tag } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  HeartOutlined,
  SmileOutlined,
  MehOutlined,
  FrownOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { getHealthScoreColor } from '../utils/healthColorUtils';
import { MAX_Z_FOR_ZERO_HEALTH } from '../utils/scoringEngineV2';
import ParameterModal from './shared/ParameterModal';
import axios from '../api';

const { Title, Text } = Typography;

const PatientHealthOverview = ({ loading, tableData, overallDeviation, patientDemographics, biologicalAge, patientId }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalData, setModalData] = useState({});
  const [biologicalAgeModalVisible, setBiologicalAgeModalVisible] = useState(false);
  const [clinicalFlags, setClinicalFlags] = useState({});

  React.useEffect(() => {
    const fetchClinicalFlags = async () => {
      if (patientId) {
        try {
          const response = await axios.get(`/patients/${patientId}/clinical-flags`);
          setClinicalFlags(response.data.data || {});
        } catch (error) {
          console.error('Failed to load clinical flags for PatientHealthOverview.');
        }
      }
    };
    fetchClinicalFlags();
  }, [patientId]);

  // Calculate overall wellness percentage
  const overallWellnessPercentage = useMemo(() => {
    return overallDeviation?.average !== null && overallDeviation?.average !== undefined
      ? Math.max(0, 100 - (overallDeviation.average / MAX_Z_FOR_ZERO_HEALTH) * 100)
      : 0;
  }, [overallDeviation]);

  // Helper function to get parameter details for biological age modal
  const getBiologicalAgeParametersData = () => {
    const biologicalAgeParams = [
      'labs_protein_albumin',
      'labs_renal_creatinine',
      'labs_glucose_glucose',
      'labs_inflammation_hscrp',
      'labs_cbc_lymph_pct',
      'labs_cbc_mcv',
      'labs_cbc_rdw',
      'labs_hepatology_alp',
      'labs_cbc_wbc'
    ];

    // Parameter display name mapping from parameters.md
    const parameterDisplayNames = {
      'labs_protein_albumin': 'Albumin (g/dL / g/L)',
      'labs_renal_creatinine': 'Creatinine (mg/dL / μmol/L)',
      'labs_glucose_glucose': 'Glucose (mg/dL / mmol/L)',
      'labs_inflammation_hscrp': 'High-Sensitivity C-Reactive Protein (mg/L)',
      'labs_cbc_lymph_pct': 'Lymphocytes (%)',
      'labs_cbc_mcv': 'Mean Corpuscular Volume (fL)',
      'labs_cbc_rdw': 'Red Cell Distribution Width (%)',
      'labs_hepatology_alp': 'Alkaline Phosphatase (U/L)',
      'labs_cbc_wbc': 'White Blood Cell Count (×10³/μL)'
    };

    const parameterDetails = [];

    if (!tableData || !Array.isArray(tableData)) {
      return { grouped: {}, flat: [] };
    }

    const allParams = tableData.flatMap(group => group.parameters || []);

    for (const paramKey of biologicalAgeParams) {
      const param = allParams.find(p => p.key === paramKey);
      if (param) {
        const priorityScore = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;
        
        let priority = 'Normal';
        if (priorityScore > 2.0) priority = 'High';
        else if (priorityScore > 1.0) priority = 'Moderate';
        else if (priorityScore > 0.5) priority = 'Low';

        // New: Extract subcategory from parameter key
        const keyParts = param.key.split('_');
        const subcategory = keyParts.length > 1 ? keyParts[1].charAt(0).toUpperCase() + keyParts[1].slice(1) : 'Lab Results';

        parameterDetails.push({
          ...param,
          name: parameterDisplayNames[param.key] || param.displayName || param.name || param.key,
          displayName: parameterDisplayNames[param.key] || param.displayName || param.name || param.key,
          specificImpact: priorityScore,
          priority: priority,
          category: 'Biological Age Lab Parameters',
          subcategory: subcategory
        });
      }
    }

    // Sort parameters by priority (highest to lowest) and then by impact score
    parameterDetails.sort((a, b) => {
      const priorityOrder = { High: 4, Moderate: 3, Low: 2, Normal: 1, 'No Data': 0 };
      const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      if (priorityDiff !== 0) return priorityDiff;

      // Within same priority, sort by impact score (highest to lowest)
      return (b.specificImpact || 0) - (a.specificImpact || 0);
    });

    return {
      grouped: { 'Biological Age Lab Parameters': parameterDetails },
      flat: parameterDetails
    };
  };

  // Calculate parameter breakdown and status counts
  const parameterStats = useMemo(() => {
    if (!tableData || !Array.isArray(tableData)) {
      return {
        totalParameters: 0,
        parametersWithData: 0,
        allClear: 0,
        lowPriority: 0,
        moderatePriority: 0,
        highPriority: 0,
      };
    }

    let totalParameters = 0;
    let parametersWithData = 0;
    let allClear = 0;
    let lowPriority = 0;
    let moderatePriority = 0;
    let highPriority = 0;

    tableData.forEach(categoryGroup => {
      (categoryGroup.parameters || []).forEach(param => {
        totalParameters++;
        
        // Count parameters with actual patient data
        if (param.currentValue !== null && param.currentValue !== undefined) {
          parametersWithData++;
          
          const weightedAbsZ = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;

          if (weightedAbsZ > 2.0) {
            highPriority++;
          } else if (weightedAbsZ > 1.0) {
            moderatePriority++;
          } else if (weightedAbsZ > 0.5) {
            lowPriority++;
          } else { // This covers weightedAbsZ <= 0.5
            allClear++;
          }
        }
      });
    });

    return {
      totalParameters,
      parametersWithData,
      allClear,
      lowPriority,
      moderatePriority,
      highPriority,
    };
  }, [tableData]);

  // Get patient-friendly health status based on wellness score
  const getWellnessStatus = (score) => {
    if (score >= 85) {
      return {
        status: 'Excellent Health',
        icon: <SmileOutlined style={{ color: '#52c41a' }} />,
        color: '#52c41a',
        message: 'Your health indicators are looking great! Keep up the excellent work with your current lifestyle.'
      };
    }
    if (score >= 70) {
      return {
        status: 'Good Health',
        icon: <SmileOutlined style={{ color: '#1890ff' }} />,
        color: '#1890ff',
        message: 'Your health is in good shape with some areas that could benefit from attention.'
      };
    }
    if (score >= 50) {
      return {
        status: 'Needs Attention',
        icon: <MehOutlined style={{ color: '#fa8c16' }} />,
        color: '#fa8c16',
        message: 'Several health areas need your attention. Focus on the recommended actions below.'
      };
    }
    return {
      status: 'Priority Focus',
      icon: <FrownOutlined style={{ color: '#ff4d4f' }} />,
      color: '#ff4d4f',
      message: 'Your health needs immediate attention. Please prioritize the action items and consult your healthcare provider.'
    };
  };

  const wellnessInfo = getWellnessStatus(overallWellnessPercentage);

  // Transform parameter data for modal display
  const transformParameterForModal = (param, categoryGroup) => {
    const priorityScore = typeof param.weightedAbsoluteZScore === 'number' ? param.weightedAbsoluteZScore : 0;
    
    let priority = 'Normal';
    if (priorityScore > 2.0) priority = 'High';
    else if (priorityScore > 1.0) priority = 'Moderate';
    else if (priorityScore > 0.5) priority = 'Low';

    return {
      ...param,
      name: param.parameterName || param.key,
      parameterName: param.parameterName || param.key,
      category: categoryGroup.categoryName || 'Unknown',
      subcategory: param.subcategory || categoryGroup.subcategory,
      specificImpact: priorityScore, // Keep specificImpact for sorting, but display as Priority Score
      priority: priority,
      zScore: param.zScore || 0,
      unit: param.unit || '',
      normalRange: param.normalRange,
      sd: param.sd,
    };
  };

  // Handle status box clicks to show filtered parameters
  const handleStatusBoxClick = (category) => {
    let paramsToDisplay = [];
    let title = '';

    const allParams = tableData.flatMap(categoryGroup =>
      (categoryGroup.parameters || []).map(param => transformParameterForModal(param, categoryGroup))
    );

    switch (category) {
      case 'total_assessed':
        title = 'All Assessed Parameters';
        paramsToDisplay = allParams.filter(p => p.currentValue !== null && p.currentValue !== undefined);
        break;
      case 'wellness':
        title = 'Complete Health Parameter Breakdown';
        paramsToDisplay = allParams;
        break;
      case 'all_clear':
        title = 'All Clear - Your Healthy Parameters';
        paramsToDisplay = allParams.filter(p => p.priority === 'Normal');
        break;
      case 'low':
        title = 'Low Priority';
        paramsToDisplay = allParams.filter(p => p.priority === 'Low');
        break;
      case 'moderate':
        title = 'Moderate Priority';
        paramsToDisplay = allParams.filter(p => p.priority === 'Moderate');
        break;
      case 'high':
        title = 'High Priority';
        paramsToDisplay = allParams.filter(p => p.priority === 'High');
        break;
      default:
        return;
    }

    // Always sort by priority score (highest first) for the flat list view
    const sortedFlatList = [...paramsToDisplay].sort((a, b) => b.specificImpact - a.specificImpact);

    // Group the filtered parameters by clinical category for the grouped view
    const groupedByCategory = paramsToDisplay.reduce((acc, param) => {
      const categoryName = param.category || 'Uncategorized';
      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }
      acc[categoryName].push(param);
      // Sort within each category as well
      acc[categoryName].sort((a, b) => b.specificImpact - a.specificImpact);
      return acc;
    }, {});

    setModalTitle(title);
    setModalData({
      grouped: groupedByCategory,
      flat: sortedFlatList,
    });
    setModalVisible(true);
  };

  if (loading) {
    return <Spin tip="Loading your health overview..." />;
  }



  return (
    <>
      <Card
        style={{
          marginBottom: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          background: '#fafafa'
        }}
      >
        <Title level={4} style={{ marginBottom: 20, color: '#2c3e50', textAlign: 'center' }}>
          <HeartOutlined style={{ marginRight: 8, color: wellnessInfo.color }} />
          Your Health Overview
        </Title>

        {/* Main Content Row */}
        <Row gutter={[24, 16]} align="middle">
          {/* Left Side - Wellness Score */}
          <Col xs={24} md={10}>
            <div
              style={{
                textAlign: 'center',
                padding: '20px',
                borderRadius: '8px',
                background: '#ffffff',
                border: `2px solid ${wellnessInfo.color}30`,
              }}
            >
              <Title level={5} style={{ marginBottom: 16, color: '#2c3e50' }}>
                Your Wellness Score
              </Title>
              <Progress
                type="circle"
                percent={Math.round(overallWellnessPercentage)}
                format={(percent) => `${percent}%`}
                strokeColor={getHealthScoreColor(overallWellnessPercentage)}
                size={120}
                trailColor="#f0f2f5"
                strokeWidth={8}
              />
              <div style={{ marginTop: 12 }}>
                <Text style={{
                  fontSize: '16px',
                  color: wellnessInfo.color,
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  {wellnessInfo.icon} {wellnessInfo.status}
                </Text>
              </div>
            </div>
          </Col>

          {/* Right Side - Status Summary */}
          <Col xs={24} md={14}>
            <Title level={5} style={{ marginBottom: 16, color: '#2c3e50' }}>
              Health Status Summary
            </Title>
            <Row gutter={[12, 12]}>
              {/* Total Assessed */}
              <Col xs={24} sm={12} md={6}>
                 <Card
                  hoverable
                  onClick={() => handleStatusBoxClick('total_assessed')}
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#f0f2f5',
                    border: '1px solid #d9d9d9',
                    cursor: 'pointer',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  styles={{ body: { padding: '12px 8px' } }}
                >
                  <DatabaseOutlined style={{ fontSize: '20px', color: '#595959', marginBottom: '4px' }} />
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{parameterStats.parametersWithData}</Title>
                  <Text style={{ fontSize: '11px' }}>Total Assessed</Text>
                </Card>
              </Col>
              {/* All Clear */}
              <Col xs={12} sm={6}>
                <Card
                  hoverable
                  onClick={() => handleStatusBoxClick('all_clear')}
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    cursor: 'pointer',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  styles={{ body: { padding: '12px 8px' } }}
                >
                  <CheckCircleOutlined style={{ fontSize: '20px', color: '#389e0d', marginBottom: '4px' }} />
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{parameterStats.allClear}</Title>
                  <Text style={{ fontSize: '11px' }}>All Clear</Text>
                </Card>
              </Col>

              {/* Low Priority */}
              <Col xs={12} sm={6}>
                <Card
                  hoverable
                  onClick={() => handleStatusBoxClick('low')}
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    cursor: 'pointer',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  styles={{ body: { padding: '12px 8px' } }}
                >
                  <InfoCircleOutlined style={{ fontSize: '20px', color: '#1890ff', marginBottom: '4px' }} />
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{parameterStats.lowPriority}</Title>
                  <Text style={{ fontSize: '11px' }}>Low Priority</Text>
                </Card>
              </Col>

              {/* Moderate Priority */}
              <Col xs={12} sm={6}>
                <Card
                  hoverable
                  onClick={() => handleStatusBoxClick('moderate')}
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                    cursor: 'pointer',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  styles={{ body: { padding: '12px 8px' } }}
                >
                  <WarningOutlined style={{ fontSize: '20px', color: '#d46b08', marginBottom: '4px' }} />
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{parameterStats.moderatePriority}</Title>
                  <Text style={{ fontSize: '11px' }}>Moderate Priority</Text>
                </Card>
              </Col>

              {/* High Priority */}
              <Col xs={12} sm={6}>
                <Card
                  hoverable
                  onClick={() => handleStatusBoxClick('high')}
                  style={{
                    textAlign: 'center',
                    backgroundColor: '#fff1f0',
                    border: '1px solid #ffa39e',
                    cursor: 'pointer',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  styles={{ body: { padding: '12px 8px' } }}
                >
                  <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#cf1322', marginBottom: '4px' }} />
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{parameterStats.highPriority}</Title>
                  <Text style={{ fontSize: '11px' }}>High Priority</Text>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Biological Age Assessment */}
      {(() => {
        if (!biologicalAge || (!biologicalAge.phenoAge && !biologicalAge.error)) {
          return (
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
                  <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age</span>
                </div>
              }
              style={{
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e8e8e8',
                marginBottom: '20px'
              }}
            >
              <Alert
                message="Biological Age Assessment Unavailable"
                description="We need comprehensive blood work to calculate your biological age. This requires several lab tests that measure how your body is aging at the cellular level."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />


            </Card>
          );
        }

        if (biologicalAge.error) {
          const isMissingBiomarkers = biologicalAge.error.startsWith("Missing one or more");
          const description = isMissingBiomarkers 
            ? "The following required biomarkers are missing from the patient's data: " + biologicalAge.error.split(':')[1]
            : biologicalAge.error;

          return (
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
                <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age Assessment</span>
                </div>
              }
              style={{
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e8e8e8',
                marginBottom: '20px'
              }}
            >
              <Alert
                message="Biological Age Assessment Unavailable"
                description={description}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Card>
          );
        }



        // Additional safety checks for nested properties
        if (!biologicalAge.phenoAge) {
          return (
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
                  <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age</span>
                </div>
              }
              style={{
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e8e8e8',
                marginBottom: '20px'
              }}
            >
              <Spin tip="Loading biological age data..." />
            </Card>
          );
        }

        // Determine which method is being used
        const isPhenoAgeOnly = biologicalAge.method === 'phenoage' || !biologicalAge.adjustedBiologicalAge;

        // Get the appropriate biological age value based on method
        let displayBiologicalAge, finalAgeDifference, methodLabel, methodBadge;
        const chronologicalAge = biologicalAge.phenoAge?.chronologicalAge;

        if (isPhenoAgeOnly) {
          // PhenoAge Only mode
          displayBiologicalAge = biologicalAge.phenoAge?.phenoAge;
          finalAgeDifference = displayBiologicalAge - chronologicalAge;
          methodLabel = 'PhenoAge - Validated';
          methodBadge = <Tag color="green" style={{ marginLeft: 8 }}>✓ Validated</Tag>;
        } else {
          // Adjusted Biological Age mode
          displayBiologicalAge = biologicalAge.adjustedBiologicalAge?.adjustedBiologicalAge;
          finalAgeDifference = displayBiologicalAge - chronologicalAge;
          methodLabel = 'Adjusted - Proprietary';
          methodBadge = <Tag color="orange" style={{ marginLeft: 8 }}>Proprietary</Tag>;
        }

        // Validation for required values
        if (displayBiologicalAge === null || displayBiologicalAge === undefined ||
            chronologicalAge === null || chronologicalAge === undefined) {
          return (
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
                  <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age</span>
                </div>
              }
              style={{
                background: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e8e8e8',
                marginBottom: '20px'
              }}
            >
              <Alert
                message="Biological Age Data Incomplete"
                description="Some required biological age calculations are missing. Please ensure all necessary lab parameters are available."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </Card>
          );
        }

        // Calculate color based on age difference
        const ageColor = finalAgeDifference < -2 ? '#52c41a' :
                        finalAgeDifference < 0 ? '#73d13d' :
                        finalAgeDifference > 2 ? '#f5222d' : '#faad14';

        // Calculate risk level based on the final age difference
        const mortRiskLevel = finalAgeDifference > 4 ? 'High' : finalAgeDifference > 0 ? 'Moderate' : 'Low';
        const mortColor = finalAgeDifference > 4 ? '#ff4d4f' : finalAgeDifference > 0 ? '#fa8c16' : '#52c41a';


        return (
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px', marginRight: '8px' }}>🧬</span>
                  <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Biological Age</span>
                </div>
                {methodBadge}
              </div>
            }
            hoverable
            onClick={() => setBiologicalAgeModalVisible(true)}
            style={{
              background: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e8e8e8',
              marginBottom: '20px',
              cursor: 'pointer'
            }}
          >
            {/* Main Biological Age Display */}
            <Row gutter={[24, 16]} justify="center">
              <Col xs={24} style={{ textAlign: 'center' }}>
                <Tooltip
                  title={
                    <div>
                      <div><strong>Method:</strong> {methodLabel}</div>
                      <div style={{ borderTop: '1px solid #666', margin: '6px 0' }}></div>
                      <div><strong>Chronological Age:</strong> {chronologicalAge.toFixed(2)} years</div>
                      <div><strong>Biological Age:</strong> {displayBiologicalAge.toFixed(2)} years</div>
                      <div style={{ borderTop: '1px solid #666', margin: '6px 0' }}></div>
                      <div><strong>Age Difference:</strong> <span style={{ color: mortColor, fontWeight: 'bold' }}>{finalAgeDifference > 0 ? '+' : ''}{finalAgeDifference.toFixed(2)} years</span> ({mortRiskLevel} Risk)</div>
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                        Click for detailed breakdown
                      </div>
                    </div>
                  }
                  placement="top"
                >
                  <div style={{
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: ageColor,
                    marginBottom: '8px',
                    cursor: 'help'
                  }}>
                    {displayBiologicalAge.toFixed(2)}
                  </div>
                </Tooltip>
                <div style={{ fontSize: '16px', color: '#666', marginBottom: '4px' }}>
                  Biological Age (years)
                </div>
                <div style={{ fontSize: '13px', color: '#999', marginBottom: '8px' }}>
                  {methodLabel}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  vs. <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{chronologicalAge.toFixed(2)}</span> actual age
                </div>
              </Col>
            </Row>
          </Card>
        );
      })()}

      {/* Biological Age Lab Parameters Modal */}
      <ParameterModal
        visible={biologicalAgeModalVisible}
        onCancel={() => setBiologicalAgeModalVisible(false)}
        title="Biological Age Details"
        parametersData={getBiologicalAgeParametersData()}
        biologicalAgeBreakdownData={biologicalAge?.adjustedBiologicalAge?.systemAdjustmentDetails}
        biologicalAgeInfo={(() => {
          // Safe extraction of biological age data for modal
          const adjustedBioAge = biologicalAge?.adjustedBiologicalAge?.adjustedBiologicalAge;
          const chronoAge = biologicalAge?.phenoAge?.chronologicalAge;
          const ageAdj = biologicalAge?.adjustedBiologicalAge?.ageAdjustment;
          const baselinePhenoAge = biologicalAge?.phenoAge?.phenoAge;
          const baselineAgeDiff = biologicalAge?.phenoAge?.ageDifference;

          // Only create biologicalAgeInfo if all required values are present
          if (adjustedBioAge !== null && adjustedBioAge !== undefined &&
              chronoAge !== null && chronoAge !== undefined &&
              ageAdj !== null && ageAdj !== undefined &&
              baselinePhenoAge !== null && baselinePhenoAge !== undefined) {

            // Calculate the adjusted age difference (adjusted biological age - chronological age)
            const adjustedAgeDifference = adjustedBioAge - chronoAge;

            const ageColor = adjustedAgeDifference < -2 ? '#52c41a' :
                           adjustedAgeDifference < 0 ? '#73d13d' :
                           adjustedAgeDifference > 2 ? '#f5222d' : '#faad14';

            return {
              adjustedBiologicalAge: adjustedBioAge,
              chronologicalAge: chronoAge,
              adjustedAgeDifference: adjustedAgeDifference,
              baselinePhenoAge: baselinePhenoAge,
              baselineAgeDifference: baselineAgeDiff || (baselinePhenoAge - chronoAge),
              ageColor: ageColor
            };
          }

          return null;
        })()}
        priorityThresholds={{
          highPriority: { impactScoreThreshold: 2.0 },
          moderatePriority: { impactScoreThreshold: 1.0 }
        }}
      />

      {/* Parameter Details Modal */}
      <ParameterModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        title={modalTitle}
        parametersData={modalData}
        priorityThresholds={{
          highPriority: { impactScoreThreshold: 2.0 },
          moderatePriority: { impactScoreThreshold: 1.0 }
        }}
      />
    </>
  );
};

export default PatientHealthOverview;
